<script>
  import { onMount, onDestroy } from "svelte";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import { InfoBlock } from "paxray-components-svelte";
  import ProcessFramework from "./ProcessFramework.svelte";
  import TeamAssignmentSelect from "./TeamAssignmentSelect.svelte";

  export let projectId;
  export let teams = [];
  export let unassignedCount = 0;
  export let assignmentsReady = false;
  export let assignedSubtaskCounts = {};
  // Per team: L1 to L3 items touched by its subtasks, plus the L4 subtasks themselves.
  export let assignedLevelBreakdown = {};

  const levels = ["process", "subprocess", "task", "subtask"];
  let loading = true;
  let error = "";
  // Nach oben gereicht, damit der Speicherstatus im Header neben dem Lock-Button steht
  export let storageError = "";
  export let loaded = false;
  export let saving = false;
  let requestVersion = 0;
  let catalogRows = [];
  let scopedIds = [];
  let drafts = {};
  let expandedProcesses = new Set();
  let search = "";
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let assignmentFilter = "";
  let selectedTeam = "";
  let sortKey = "";
  let sortDirection = "none";
  let page = 1;
  let pageSize = 25;
  let filterKey = 0;
  async function readAssignments(response) {
    const data = await response.json();
    if (!response.ok)
      throw new Error(
        data.message || "Team assignments could not be loaded or saved.",
      );
    if (
      !Array.isArray(data.subtask_ids) ||
      data.subtask_ids.some((id) => typeof id !== "string") ||
      !data.assignments ||
      Array.isArray(data.assignments) ||
      typeof data.assignments !== "object" ||
      Object.values(data.assignments).some(
        (ids) =>
          !Array.isArray(ids) || ids.some((id) => typeof id !== "string"),
      )
    ) {
      throw new Error("Invalid team assignment response.");
    }
    return data;
  }

  async function loadFramework() {
    const version = ++requestVersion;
    loading = true;
    loaded = false;
    error = storageError = "";
    try {
      const paths = [
        `team-collection/${projectId}`,
        "departments",
        "processes",
        "subprocesses",
        "tasks",
        "subtasks",
      ];
      const [
        collection,
        departments,
        processes,
        subprocesses,
        tasks,
        subtasks,
      ] = await Promise.all(
        paths.map(async (path, index) => {
          const response = await fetchWithSession(`/api/damprojects/${path}`, {
            cache: "no-store",
          });
          if (index === 0) return readAssignments(response);
          if (!response.ok)
            throw new Error("Framework could not be loaded. Please try again.");
          return response.json();
        }),
      );
      if (version !== requestVersion) return;
      scopedIds = collection.subtask_ids;
      drafts = collection.assignments;
      const processMap = new Map(
        processes.map((item) => [String(item.id), item]),
      );
      const subprocessMap = new Map(
        subprocesses.map((item) => [String(item.id), item]),
      );
      const taskMap = new Map(tasks.map((item) => [String(item.id), item]));
      catalogRows = subtasks.flatMap((subtask) => {
        const task = taskMap.get(String(subtask.task_id));
        const subprocess = subprocessMap.get(String(task?.subprocess_id));
        const process = processMap.get(String(subprocess?.process_id));
        const linked = departments.filter(
          (department) =>
            (department.processes || []).some(
              (item) => String(item.id) === String(process?.id),
            ) ||
            (process?.departments || []).some(
              (item) => String(item.id) === String(department.id),
            ),
        );
        return (linked.length ? linked : [null]).map((department) => ({
          id: `${department?.id || "unassigned"}:${subtask.id}`,
          department,
          process,
          subprocess,
          task,
          subtask,
        }));
      });
      loaded = true;
    } catch (err) {
      if (version === requestVersion)
        error = err.message || "Framework could not be loaded.";
    } finally {
      if (version === requestVersion) loading = false;
    }
  }

  onMount(loadFramework);
  onDestroy(() => {
    requestVersion += 1;
  });

  $: explicitScope = new Set(scopedIds);
  $: scopedRows = catalogRows.filter((row) =>
    explicitScope.has(String(row.subtask.id)),
  );
  // A subtask can occur under several departments; show it only once here.
  $: rows = [
    ...new Map(
      scopedRows.map((row) => [
        String(row.subtask.id),
        { ...row, id: String(row.subtask.id) },
      ]),
    ).values(),
  ];
  $: scopeIds = new Set(rows.map((row) => String(row.subtask.id)));
  $: effectiveAssignments = getAssignments(teams, drafts, scopeIds);
  $: assignedSubtaskCounts =
    loaded && !loading && !error
      ? Object.fromEntries(
          [...effectiveAssignments].map(([teamId, ids]) => [teamId, ids.size]),
        )
      : {};
  $: assignedLevelBreakdown =
    loaded && !loading && !error
      ? Object.fromEntries(
          [...effectiveAssignments].map(([teamId, ids]) => [
            teamId,
            getLevelBreakdown(rows.filter((row) => ids.has(row.id))),
          ]),
        )
      : {};
  $: unassignedIds = new Set(
    [...scopeIds].filter(
      (id) => ![...effectiveAssignments.values()].some((ids) => ids.has(id)),
    ),
  );
  $: unassignedCount = unassignedIds.size;
  $: assignmentsReady =
    loaded &&
    !loading &&
    !saving &&
    !error &&
    !storageError &&
    scopeIds.size > 0;
  // Muss vor "options" stehen: Svelte zieht "filters" davor, ein zurueckgesetzter Filter greift sonst erst im naechsten Update
  $: clearInvalidFilters(rows, selectedProcess, selectedSubprocess);
  // Untere Ebenen zeigen nur Eintraege, die zu den gewaehlten uebergeordneten Ebenen passen
  $: options = Object.fromEntries(
    levels.map((level, index) => [
      level,
      [
        ...new Map(
          rows
            .filter(
              (row) =>
                row[level] &&
                levels
                  .slice(0, index)
                  .every(
                    (parent) =>
                      !filters[parent] ||
                      String(row[parent]?.id) === String(filters[parent]),
                  ),
            )
            .map((row) => [
              String(row[level].id),
              { key: row[level].id, value: row[level].name },
            ]),
        ).values(),
      ].sort((a, b) => a.value.localeCompare(b.value)),
    ]),
  );
  $: filters = {
    process: selectedProcess,
    subprocess: selectedSubprocess,
    task: selectedTask,
    subtask: selectedSubtask,
  };
  $: teamOptions = teams.map((team) => ({
    key: String(team.id),
    value: team.name,
  }));
  $: filteredRows = rows.filter((row) => {
    const id = String(row.subtask.id);
    const matchesAssignment =
      !assignmentFilter ||
      (assignmentFilter === "unassigned"
        ? unassignedIds.has(id)
        : !unassignedIds.has(id));
    return (
      matchesAssignment &&
      (!selectedTeam ||
        Boolean(effectiveAssignments.get(selectedTeam)?.has(id))) &&
      levels.every(
        (level) =>
          !filters[level] || String(row[level]?.id) === String(filters[level]),
      ) &&
      levels.some((level) =>
        row[level]?.name
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase()),
      )
    );
  });
  $: visibleRows = getVisibleRows(
    filteredRows,
    rows,
    expandedProcesses,
    sortKey,
    sortDirection,
  );
  $: {
    search;
    selectedProcess;
    selectedSubprocess;
    selectedTask;
    selectedSubtask;
    assignmentFilter;
    selectedTeam;
    sortKey;
    sortDirection;
    pageSize;
    page = 1;
  }
  $: if (page > Math.max(1, Math.ceil(visibleRows.length / pageSize)))
    page = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  $: pagedRows = visibleRows.slice((page - 1) * pageSize, page * pageSize);

  // Groups each level by its parent, e.g. L3 tasks under their L2 subprocess.
  function getLevelBreakdown(teamRows) {
    const collect = (level, parentLevel) => {
      const groups = new Map();
      const seen = new Set();
      teamRows.forEach((row) => {
        const item = row[level];
        if (!item || seen.has(String(item.id))) return;
        seen.add(String(item.id));
        const parent = parentLevel ? row[parentLevel]?.name || "Other" : "";
        if (!groups.has(parent)) groups.set(parent, []);
        groups.get(parent).push(item.name);
      });
      const sortedGroups = [...groups]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, items]) => ({ name, items: items.sort() }));
      return { count: seen.size, groups: sortedGroups };
    };
    return {
      L1: collect("process"),
      L2: collect("subprocess", "process"),
      L3: collect("task", "subprocess"),
      L4: collect("subtask", "task"),
    };
  }

  function getAssignments(teamList, saved, allowedIds) {
    return new Map(
      teamList.map((team) => {
        const teamId = String(team.id);
        return [
          teamId,
          new Set((saved[teamId] || []).filter((id) => allowedIds.has(id))),
        ];
      }),
    );
  }

  // Clicks are shown at once and saved one after another, so a second click while saving is not lost
  let saveQueue = Promise.resolve();
  let pendingSaves = 0;
  let confirmedDrafts = {};

  function assignTeam(teamId, targetIds, checked) {
    if (!loaded || loading || storageError) return;
    const version = requestVersion;
    if (!pendingSaves) confirmedDrafts = drafts;
    const changedIds = [...new Set(targetIds)];
    const nextIds = new Set(drafts[teamId] || []);
    changedIds.forEach((id) =>
      checked ? nextIds.add(id) : nextIds.delete(id),
    );
    drafts = { ...drafts, [teamId]: [...nextIds] };
    pendingSaves += 1;
    saving = true;
    saveQueue = saveQueue.then(() =>
      saveTeamChange(teamId, changedIds, checked, version),
    );
  }

  async function saveTeamChange(teamId, changedIds, checked, version) {
    try {
      // After an error or a reload the remaining queued clicks are dropped, the shown state is reset anyway
      if (version !== requestVersion || storageError) return;
      const saved = await readAssignments(
        await fetchWithSession(
          `/api/damprojects/team-collection/${projectId}/${teamId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              add_subtask_ids: checked ? changedIds : [],
              remove_subtask_ids: checked ? [] : changedIds,
            }),
          },
        ),
      );
      if (version !== requestVersion) return;
      scopedIds = saved.subtask_ids;
      confirmedDrafts = saved.assignments;
      // Earlier answers must not undo clicks that are still queued
      if (pendingSaves === 1) drafts = saved.assignments;
    } catch (err) {
      if (version !== requestVersion) return;
      drafts = confirmedDrafts;
      storageError = `${err.message || "Team assignments could not be saved."} Reload to check the saved assignments.`;
    } finally {
      pendingSaves -= 1;
      if (version === requestVersion && !pendingSaves) saving = false;
    }
  }

  function getVisibleRows(filtered, allRows, expanded, key, direction) {
    const groups = new Map();
    filtered.forEach((row) => {
      const id = row.process?.id || "unassigned";
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(row);
    });
    const compare = (a, b) =>
      (a[key]?.name || "").localeCompare(b[key]?.name || "", undefined, {
        numeric: true,
      }) * (direction === "descending" ? -1 : 1);
    const entries = [...groups.entries()].sort((a, b) =>
      key === "process" && direction !== "none"
        ? compare(a[1][0], b[1][0])
        : (a[1][0].process?.name || "").localeCompare(
            b[1][0].process?.name || "",
          ),
    );
    return entries.flatMap(([departmentKey, group], groupIndex) => {
      const departmentRows = allRows.filter(
        (row) => (row.process?.id || "unassigned") === departmentKey,
      );
      const departmentSummary = {
        counts: Object.fromEntries(
          levels.map((level) => [
            level,
            new Set(
              departmentRows
                .filter((row) => row[level])
                .map((row) => row[level].id),
            ).size,
          ]),
        ),
        missing: [],
      };
      const sorted = [...group].sort(
        key && direction !== "none"
          ? compare
          : (a, b) =>
              levels
                .map((level) => a[level]?.name || "")
                .join("\0")
                .localeCompare(
                  levels.map((level) => b[level]?.name || "").join("\0"),
                ),
      );
      return (expanded.has(departmentKey) ? sorted : sorted.slice(0, 1)).map(
        (row, index) => ({
          ...row,
          departmentKey,
          departmentSummary,
          isDepartmentStart: index === 0,
          isDepartmentCollapsed: !expanded.has(departmentKey),
          isAlternateGroup: groupIndex % 2 === 1,
        }),
      );
    });
  }

  // Gewaehlter Subprocess oder Task, der nicht mehr zum uebergeordneten Filter passt, wird zurueckgesetzt
  function clearInvalidFilters(allRows, process, subprocess) {
    const matches = (row, level, value) => !value || String(row[level]?.id) === String(value);
    if (subprocess && !allRows.some((row) => matches(row, "subprocess", subprocess) && matches(row, "process", process))) selectedSubprocess = "";
    if (selectedTask && !allRows.some((row) => matches(row, "task", selectedTask) && matches(row, "process", process) && matches(row, "subprocess", selectedSubprocess))) selectedTask = "";
  }

  function toggleProcess(id) {
    const next = new Set(expandedProcesses);
    next.has(id) ? next.delete(id) : next.add(id);
    expandedProcesses = next;
    page = 1;
  }

  function resetFilters() {
    search =
      selectedProcess =
      selectedSubprocess =
      selectedTask =
      selectedSubtask =
      assignmentFilter =
      selectedTeam =
        "";
    filterKey += 1;
  }
</script>

<svelte:window
  on:focus={() => {
    if (!loading && !saving) loadFramework();
  }}
/>

<!-- Ohne Team gibt es nichts zuzuweisen: Tabelle und Warnung erst zeigen, wenn ein Team existiert. Der Hinweis
     "Team setup" oben deckt den leeren Zustand ab; die Komponente bleibt gemountet, damit die Bindings aktuell bleiben. -->
{#if teams.length}
  <section
    class="team-subtask-assignment"
    aria-label="Assign subtasks to teams"
  >
    {#if loading}
      <p role="status">Loading project framework…</p>
    {:else if error}
      <InfoBlock
        type="warning"
        heading="Framework unavailable"
        message={error}
        showClose={false}
      />
      <button type="button" on:click={loadFramework}>Try again</button>
    {:else}
      <InfoBlock
        type={unassignedIds.size ? "warning" : scopeIds.size ? "hint" : "info"}
        heading={"Assign subtasks (L4) to teams · " +
          (unassignedIds.size
            ? `${unassignedIds.size} of ${scopeIds.size} subtasks have no team`
            : scopeIds.size
              ? "All subtasks assigned to teams"
              : "No subtasks selected")}
        message="Select teams for each subtask or assign an entire process at once. Only subtasks selected in the project framework are shown. Red rows have no team assignment."
        showClose={false}
      />
      {#if !explicitScope.size}<p class="assignment-note">
          No subtasks selected for this project. Select subtasks in Configure
          Framework first.
        </p>{/if}
      {#if storageError}
        <InfoBlock
          type="warning"
          heading="Team assignments could not be saved"
          message={storageError}
          showClose={false}
        />
        <button type="button" on:click={loadFramework}
          >Reload assignments</button
        >
      {/if}
      <ProcessFramework
        teamAssignment
        {projectId}
        companyFrameworkRows={rows}
        companyFrameworkPagedRows={pagedRows}
        visibleCompanyFrameworkRows={visibleRows}
        unassignedSubtaskIds={unassignedIds}
        listOfProcesses={options.process}
        listOfSubprocesses={options.subprocess}
        listOfTasks={options.task}
        bind:selectedProcess
        bind:selectedSubprocess
        bind:selectedTask
        bind:assignmentFilter
        listOfTeams={teamOptions}
        bind:selectedTeam
        bind:companyFrameworkSearch={search}
        bind:companyFrameworkSortKey={sortKey}
        bind:companyFrameworkSortDirection={sortDirection}
        bind:companyFrameworkPage={page}
        bind:companyFrameworkPageSize={pageSize}
        {filterKey}
        {resetFilters}
        toggleCompanyDepartment={toggleProcess}
      >
        <svelte:fragment
          slot="team-assignment"
          let:targetIds
          let:label
          let:group
          let:level
          let:variant
        >
          <TeamAssignmentSelect
            disabled={!loaded || Boolean(storageError)}
            {teams}
            {targetIds}
            {label}
            {group}
            {level}
            {variant}
            assignments={effectiveAssignments}
            onAssign={assignTeam}
          />
        </svelte:fragment>
      </ProcessFramework>
    {/if}
  </section>
{/if}

<style>
  .team-subtask-assignment {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }
  .team-subtask-assignment :global(.warning-notice) {
    margin-bottom: 0;
  }
  .assignment-note {
    margin: 0;
    font-size: 0.8125rem;
  }
</style>
